import type { LandingContent } from "./types";
import heroImage from "@/assets/hero-house.webp";
import cavityCrossSection from "@/assets/lp/diagrams/cavity-cross-section.webp";

import bondedAssessed from "@/assets/lp/benefits/removes-damp.webp";
import bondedThermal from "@/assets/lp/benefits/restores-thermal.webp";
import surveyorInspectingWall from "@/assets/lp/benefits/surveyor-inspecting-wall.webp";
import bondedBreathable from "@/assets/lp/benefits/controlled-external.webp";

import processPropertyAssessment from "@/assets/process/property-assessment.webp";
import processSitePreparation from "@/assets/process/site-preparation.webp";
import processFinalCheck from "@/assets/process/final-cavity-check.webp";
import quotationScheduleOfWorks from "@/assets/lp/quotation-schedule-of-works.webp";

const PATH = "/lp/cavity-wall-insulation" as const;

/** Primary quotation CTA label, consistent across hero, strips, and in-page links. */
export const CAVITY_INSTALL_QUOTATION_CTA = "Book A Free Quotation" as const;

/** @deprecated Use CAVITY_INSTALL_QUOTATION_CTA */
export const CAVITY_INSTALL_INSPECTION_CTA = CAVITY_INSTALL_QUOTATION_CTA;

/** Shown near cavity CTAs; paid report is optional, not required for a quote. */
export const CAVITY_INSPECTION_REPORT_NOTE =
  "A written cavity wall inspection report is available from £99 on request, not required for a free quotation on install or extraction work." as const;

/**
 * Body stack for `LpOrderedSections` on the cavity wall insulation LP.
 *
 * Page story: symptoms (custom hero/why in `lp-cavity-wall-insulation.tsx`) → survey/process →
 * installation day → completion pack → suitability/failure signals → bonded bead → trust → quote → FAQ → form.
 *
 * `failureAccordion` renders inside the product section; it is omitted from `sectionOrder`.
 *
 * Fields below marked @legacy satisfy `LandingContent` but are not in `sectionOrder` , the custom page
 * and `processClarity` carry that narrative instead (avoids duplicate sections and scroll bloat).
 */
export const cavityWallInsulationInstallLpBody: LandingContent = {
  pageKind: "cavity-install",
  slug: "cavity-wall-insulation",
  path: PATH,
  title: "Cavity Wall Insulation In South Wales",
  metaTitle: "Cavity Wall Insulation South Wales | PAS 2030 Certified",
  metaDescription:
    "PAS 2030 certified cavity wall insulation installers in South Wales. Free no-obligation quotation, 25-year guarantee. Call 01656 334698.",
  defaultService: "Cavity Wall Insulation",
  heroHeadline: "Cavity Wall Insulation In South Wales",
  heroSubheading:
    "Warmer rooms, lower heat loss, and a more comfortable home across Wales and the South of England, installed by a PAS 2030 certified team and registered with a 25-year guarantee.",
  heroBullets: [],
  heroImage,
  heroAlt:
    "South Wales home benefiting from CRS Energy PAS 2030 bonded-bead cavity wall insulation",
  heroFloatImages: [
    { src: heroImage, slug: "cwi-install-hero" },
    { src: cavityCrossSection, slug: "cwi-install-cross" },
    { src: bondedAssessed, slug: "cwi-assessed" },
    { src: bondedThermal, slug: "cwi-thermal" },
    { src: surveyorInspectingWall, slug: "cwi-surveyor" },
    { src: bondedBreathable, slug: "cwi-breathable" },
    { src: processPropertyAssessment, slug: "cwi-property-assess" },
    { src: processSitePreparation, slug: "cwi-site-prep" },
    { src: processFinalCheck, slug: "cwi-final-check" },
    { src: quotationScheduleOfWorks, slug: "cwi-quotation" },
  ],
  trustItems: [
    "PAS 2030 Certified Installations",
    "25-Year Guarantee",
    "Insurance-Backed Warranty",
    "Free No-Obligation Quotation",
    "No Obligation Quote",
  ],

  /** @legacy Rendered via custom “Why Cavity Wall Insulation Matters” section , not in sectionOrder. */
  problemTitle: "Common Drivers For Cavity Wall Insulation Projects",
  problemPoints: [
    {
      title: "Empty Cavity Voids",
      body: "Many post-1920 homes still leak heat through unfilled cavities. Inspection confirms where injection is viable.",
    },
    {
      title: "Uncomfortable External Walls",
      body: "Cold plaster, patchy heating response and stubborn condensation often improve once suitable cavities are insulated.",
    },
    {
      title: "Rising Energy Demand",
      body: "Insulating large wall areas is one of the most cost-effective ways to reduce heat flux alongside loft top-ups.",
    },
  ],
  clarityTitle: "How Bonded-Bead Cavity Insulation Works",
  clarityBody:
    "Small injection holes are formed in the mortar bed, beads are pneumatically installed, then joints are re-pointed. The process is external-first and typically completes within a day for many property types once suitability is proven.",
  clarityImage: cavityCrossSection,
  clarityImageAlt:
    "Cross-section diagram of cavity wall with bonded EPS beads between inner and outer leaves",
  productTitle: "Why Bonded Beads For New Cavity Fills",
  productPoints: [
    {
      title: "Engineered Adhesive Cohesion",
      body: "Bonded EPS resists slump compared with legacy fibre systems when installed in compliant cavities.",
    },
    {
      title: "Water-Repellent Chemistry",
      body: "Beads are treated for driving rain exposure once exposure calculations allow installation.",
    },
    {
      title: "Documented PAS 2030 Trail",
      body: "Inspection notes, injection logs and guarantee packs support mortgage and EPC conversations.",
    },
    {
      title: "Minimal Internal Disruption",
      body: "Work is sequenced from outside with controlled dust management and reinstatement photos.",
    },
  ],
  processSteps: [
    {
      step: 1,
      title: "Technical Inspection",
      description:
        "We assess cavity width, ties, ventilation, damp risk, exposure and suitability before recommending bonded-bead injection.",
      icon: "clipboard",
    },
    {
      step: 2,
      title: "Quotation & Plan",
      description:
        "A friendly advisor discusses inspection results, timelines and your written PAS 2030 quotation with no obligation to proceed.",
      icon: "file",
    },
    {
      step: 3,
      title: "External Installation",
      description:
        "Our certified installers inject bonded EPS beads via controlled external bore holes, completing most homes inside a single day.",
      icon: "drill",
    },
    {
      step: 4,
      title: "QC, Guarantees & Aftercare",
      description:
        "Post-install verification, guarantee registration and insurance-backed warranty documents are handed over with PAS 2030 evidence.",
      icon: "certificate",
    },
  ],
  qualificationTitle: "Signals we look for on site",
  qualificationItems: [
    "Brick or block cavity leaves with verifiable void",
    "Solid wall ties without severe corrosion",
    "Manageable exposure to driving rain",
    "Effective ventilation paths or mitigations agreed",
    "No active uncontrolled water ingress",
    "Client awareness of programme and access needs",
  ],
  includedItems: [
    "PAS 2030 technical inspection prior to installation",
    "EPS bonded bead material specified for assessed cavities",
    "Injection installation from externally accessed drill pattern",
    "Post-completion quality assurance checks",
    "Building regulations acknowledgement where schemes apply",
    "25-year guarantee registration",
    "Insurance-backed peace-of-mind documentation",
  ],
  trustSectionTitle: "Inspection-Led Recommendations Every Time",
  trustSectionBody:
    "CRS Energy specialises in diagnosing what your building actually needs. If bonded-bead cavity insulation is inappropriate, we'll explain why and map out remediation, ventilation tweaks or cavity extraction before any fill is contemplated. PAS 2030 governance and independent guarantees apply to compliant installs.",

  benefits: [
    {
      title: "Warmer, More Predictable Room Temperatures",
      description:
        "Even coverage inside the cavity helps reduce chilly external walls once your home is judged suitable.",
    },
    {
      title: "Lower Heat Loss Alongside Other Retrofit Measures",
      description:
        "Cavity wall insulation remains one of the most cost-effective large-area upgrades on many post-1920 homes.",
    },
    {
      title: "Controlled External Process",
      description:
        "Minimal Internal Disruption whilst beads are pneumatically pumped and joints are professionally re-pointed.",
    },
    {
      title: "Clear Documentation Trail",
      description:
        "Inspection notes, borescope evidence, commissioning photos and installer declarations give buyers confidence.",
    },
  ],


  faqs: [
    {
      question: "How much does cavity wall insulation cost?",
      answer:
        "Cost depends on your home's heated floor area, construction type and access logistics. Published price lists rarely reflect South Wales exposures, so after a PAS 2030 inspection you receive a bespoke itemised quotation per m², with no obligation to proceed.",
    },
    {
      question: "Is my home suitable?",
      answer:
        "Most brick cavity dwellings built after the 1920s can be insulated, though exposure to driving rain, narrow cavities, bridging debris or failed historic fills means some properties must be remediated or partially extracted before injection. Inspection scope proves suitability before mobilisation.",
    },
    {
      question: "How long does installation take?",
      answer:
        "Many semis and terraces finish within one working day. Larger detached homes might extend slightly but remain an external scaffold-led process with limited internal disruption.",
    },
    {
      question: "Will insulation cause damp problems?",
      answer:
        "Correctly specified bonded-bead systems use water-repellent EPS with breathable detailing in compliant cavities and should not replicate older fibre slump issues. Where risk factors exist our survey stops the programme until corrective work is scoped.",
    },
    {
      question: "Will I notice energy savings?",
      answer:
        "Savings hinge on tariff, occupancy and heating efficiencies, but insulating an empty cavity materially lowers heat flux through masonry, and many homeowners report shorter warm-up cycles and steadier comfort.",
    },
    {
      question: "What warranties apply?",
      answer:
        "Qualified installs register for a manufacturer-backed system guarantee up to 25 years plus insurance-backed warranty paperwork for additional consumer protection.",
    },
    {
      question: "Do I need planning permission?",
      answer:
        "Permitted Development typically covers retrospective cavity insulation in domestic settings, although listed buildings or conservation areas may vary. We clarify during surveying.",
    },
    {
      question: "What if cavity extraction is recommended instead?",
      answer:
        "When historic insulation jeopardises structural breathability we coordinate PAS 2030 extraction first and only reinject once the cavity can perform. That avoids contradictory advice across trades.",
    },
  ],

  ctaTitle: "Book Your Free Cavity Wall Quotation",

  testimonialsFull: [
    {
      name: "Liz S",
      date: "February 2025",
      serviceTag: "Cavity Wall Insulation",
      source: "google",
      quote:
        "We had cavity wall insulation done which has made a big difference to our house. Our rooms were cold and we had some mould in corners of the rooms. Since having it done there hasn't been any. House warms up quicker and stays warmer longer. So glad we had it done and it was quick and easy. CRS were lovely and I would recommend them to anyone.",
    },
    {
      name: "Kevin Stevens",
      date: "September 2024",
      serviceTag: "Boiler & Wall Insulation",
      source: "trustpilot",
      quote:
        "This company carried out the work on our house for a Welsh government grant scheme, the work was carried out between July and September for a new boiler and internal wall and cavity wall insulation. Every one of the workers in the different teams, including the plumbers, electricians, insulation team, carpenters, plasterers, and the painter were all very polite, friendly and respectful of our property. We would highly recommend them.",
    },
    {
      name: "Richard W",
      date: "September 2025",
      serviceTag: "Whole Home Retrofit",
      source: "google",
      quote:
        "I cannot praise this company too highly. From first contact to completion of the work the whole experience was perfect. People turned up when they were supposed to and were all friendly, good-humoured and respectful, and they knew what they were doing. All three teams that carried out the work were well coordinated and efficient. And they all cleared up after them! Great coordination between the office and the survey and work teams.",
    },
  ],

  failureAccordion: {
    title: "Does Any Of This Sound Familiar?",
    subtitle: "Below are the patterns we see most often across South Wales before we recommend bonded bead insulation.",
    premium: {
      eyebrow: "What We Often See On Site",
      title: "What Colder Homes Often Have In Common",
      body:
        "We've visited many homes where certain rooms never seem to stay warm for long, even when the heating is working. Sometimes the issue is linked to [[a:missing insulation]], [[p:older materials]], [[a:patchy coverage]], or [[p:external walls]] that lose warmth quickly in exposed weather. These patterns are often noticed first as [[a:cold walls]], [[p:slow-heating rooms]], [[a:condensation patches]], or [[p:draughty-feeling areas]] near outside walls.",
      image: surveyorInspectingWall,
      imageAlt: "CRS Energy surveyor inspecting an external cavity wall with the homeowner",
      clarityLinkText: "Why Bonded Bead Insulation",
      clarityHref: "#why-bonded-bead",
    },
    diagnosticCards: [
      {
        iconKey: "thermometer-snowflake",
        title: "Rooms Do Not Retain Heat",
        description:
          "Heat escapes quickly through [[a:unfilled cavities]], which results in [[p:Uninsulated cavity walls]] even after you've turned the heating up.",
        signs: ["Chilly Plaster", "Uneven TRV Response", "Cold Corners", "Thermostat Creep"],
        why: "Bonded bead [[a:fills the cavity evenly]] where suitable so emitters regain control without chasing boiler upgrades.",
      },
      {
        iconKey: "droplets",
        title: "Condensation Lingering By External Walls",
        description:
          "Warm indoor air meets [[a:colder surfaces]] near external walls. [[p:Uninsulated cavity walls]] make those surfaces colder for longer.",
        signs: ["Dew On Paint", "Mould Hotspots", "Peeling Wallpaper", "Musty Winters"],
        why: "Better insulated surfaces [[a:lift temperatures]] whilst we flag ventilation tweaks whenever inspections suggest they're needed.",
      },
      {
        iconKey: "alert-triangle",
        title: "Told Your Cavity Walls Are Too Narrow?",
        description:
          "Older properties are often ruled out too quickly. We check the cavity properly so you know whether [[a:modern bonded bead insulation]] may be suitable for your home.",
        signs: ["Previously Told No", "Narrow Cavity Concern", "Older Property", "Cold External Walls", "Suitability Check Needed"],
        why: "Many older properties were previously ruled out for cavity wall insulation because the cavity was considered too small. [[a:Modern bonded bead systems]] may now be suitable for some narrow cavities, but only where the wall condition, exposure, and cavity width are right. That is why we inspect first before recommending any installation.",
      },
      {
        iconKey: "alert-octagon",
        title: "One Side Of The House Feels Colder",
        description:
          "Some homes have one wall or room that always seems harder to keep warm, especially side walls, gable ends, or rooms facing open weather. Homeowners often notice that these areas feel colder, take longer to heat up, or are more prone to [[a:condensation]] after wet or windy weather.",
        signs: ["One Room Feels Colder", "Cold Side Wall", "Draughty External Wall", "Condensation After Rain", "Harder To Heat"],
        why: "If one side of the property is more exposed, we need to check the wall and cavity properly before recommending insulation. The wall may still be suitable, but exposure, moisture, and external condition all need to be considered first.",
      },
    ],
    rows: [
      {
        icon: "thermometer-snowflake",
        title: "Comfort Imbalance",
        teaser: "",
        body: "",
        symptomTiles: [
          { icon: "snowflake", label: "Plaster stays cold hours after heating" },
          { icon: "thermometer", label: "Thermostats need higher setpoints" },
          { icon: "flame", label: "Boiler firing longer than neighbours" },
          { icon: "clock", label: "Slow warmup after work trips" },
        ],
        image: bondedThermal,
        imageAlt:
          "Family enjoying warmer living space after bonded-bead cavity wall insulation installation",
      },
      {
        icon: "droplets",
        title: "Moisture Balance Checks",
        teaser: "",
        body: "",
        symptomTiles: [
          { icon: "droplets", label: "Damp spotting on sheltered bedrooms" },
          { icon: "circle-dashed", label: "Mould returning rapidly after bleach" },
          { icon: "paintbrush", label: "Peeling lining paper on gables" },
          { icon: "wind", label: "Trickle vents always closed for noise" },
        ],
        image: bondedAssessed,
        imageAlt: "CRS inspector discussing ventilation alongside cavity suitability checks",
      },
      {
        icon: "alert-triangle",
        title: "Historic Retrofit Backlog",
        teaser: "",
        body: "",
        symptomTiles: [
          { icon: "calendar-clock", label: "Never cavity insulated since build" },
          { icon: "file-search", label: "Prior EPC cites empty cavity walls" },
          { icon: "target", label: "Loft insulated but walls untouched" },
          { icon: "paintbrush", label: "Older pilot holes never completed" },
        ],
        image: cavityCrossSection,
        imageAlt: "Technical illustration of cavity awaiting bonded bead injection after inspection clearance",
      },
      {
        icon: "alert-octagon",
        title: "Severely Exposed Elevations",
        teaser: "",
        body: "",
        symptomTiles: [
          { icon: "mountain", label: "Hilltop turbulence" },
          { icon: "wind", label: "Prevailing onshore gusts" },
          { icon: "minus", label: "Open perp joints sponging moisture" },
          { icon: "grip", label: "Older render trapping salinity" },
        ],
        image: bondedBreathable,
        imageAlt:
          "Close-up emphasizing breathable bead pack protecting cavity skins in exposed exposures",
      },
    ],
    ambiguityCta: {
      title: "Want A Straight Answer About Your Walls?",
      body:
        "Tell us what you're seeing and we'll book an [[a:honest inspection]]. We'll either map a [[p:bonded bead install]] that matches the cavity you've got, or explain why [[a:damp work or extraction]] needs to come first.",
    },
    ctaHref: "#bottom-cta",
  },

  processClarity: {
    title: "Our Cavity Wall Insulation Process",
    intro:
      "Before any insulation is installed, we make sure your property is [[a:suitable for cavity wall insulation]]. Our team checks the [[p:condition of the walls]], plans the correct installation approach, and provides [[a:clear evidence]] once everything is finished.",
    diagramImage: cavityCrossSection,
    diagramAlt: "Diagram of cavity walls fully filled with bonded EPS bead insulation.",
    stepTexts: [
      "Thermal and structural surveying clarifies condensation, bridging and cavity continuity.",
      "Programmes integrate scaffold, sequencing and RAMS tailored to bonded-bead OEM data.",
      "Injection rigs pressure-feed adhesive-coated beads whilst damp-proof reinstatement completes each elevation.",
      "Commissioning packs bundle borescope grabs, QA sign-off and insurer-ready warranty uploads.",
    ],
    flowSteps: [
      {
        n: "01",
        title: "Suitability Survey",
        desc: "Our friendly surveyor will thoroughly inspect all elevations (sides) of your property to determine if our bonded bead insulation system is the best choice for you. They will use a [[p:specialised camera]] to take photos during the inspection, ensuring that the cavity void is [[a:clean, clear, and suitable]] for installation. This information will be communicated to our office to prepare your quotation.",
        icon: "stethoscope",
        image: processPropertyAssessment,
        imageAlt: "PAS 2030 cavity wall surveyor inspecting with borescope tooling",
        checks: [
          "Wall Tie Condition Inspection",
          "Rubble Or Debris Presence",
          "Wall Suitability",
        ],
        checksPill:
          "If the survey flags anything, we can usually put it right before we insulate. We'll make that clear in your quote.",
      },
      {
        n: "02",
        title: "Quotation & Schedule Of Works",
        desc: "After your suitability survey, we prepare a [[a:clear quotation]] and schedule of works so you know [[p:what is included]], how the installation will be completed, and what needs to happen before work begins.",
        icon: "file",
        image: quotationScheduleOfWorks,
        imageAlt:
          "CRS surveyor in high-vis reviewing quotation and schedule of works on a clipboard at a residential property",
        checks: [
          "Areas Being Insulated",
          "Access Or Scaffolding Requirements",
          "Any Preparation Needed Before Installation",
          "Expected Timescale And Booking Date",
        ],
        checksPill:
          "Any additional remedial work, access requirements or preparation will be explained separately, so you understand what is included before the installation goes ahead.",
      },
      {
        n: "03",
        title: "Installation",
        desc: "Once everything is agreed, our team installs the cavity wall insulation [[a:from outside the property]]. The work is carried out carefully through small external drill holes, with the insulation installed providing an [[p:even fill]] across the property.",
        icon: "brick",
        image: processSitePreparation,
        imageAlt: "CRS crew installing cavity wall insulation from outside the property",
        checks: [
          "Agreed Walls Are Insulated",
          "Small External Access Points Used",
          "Insulation Installed Evenly",
          "Work Area Kept Tidy",
          "Drill Points Made Good After Installation",
        ],
        checksPill:
          "Our team works from outside wherever possible, helping to keep disruption inside your home to a minimum while the installation is completed. If internal work is required, this will be confirmed within the survey stage.",
        detailLinkHref: "#installation-day",
        detailLinkText: "See What Happens On Installation Day",
      },
      {
        n: "04",
        title: "Aftercare & Guarantees",
        desc: "After installation, we complete [[a:final checks]] and provide the relevant paperwork for your records. You will receive confirmation of the work completed, any [[p:guarantee information]], and guidance on what to expect after the installation.",
        icon: "shield",
        image: processFinalCheck,
        imageAlt: "Final installation checks and completion paperwork with the homeowner",
        checks: [
          "Final Installation Checks Completed",
          "Work Areas Reviewed Before Leaving",
          "Guarantee Information Provided",
          "Completion Paperwork Issued",
          "Aftercare Advice Explained",
        ],
        checksPill:
          "You will know what has been completed, what documentation applies, and who to contact if you have any questions after the work is finished.",
      },
    ],
    facts: [
      { icon: "clock", label: "Typical Onsite Window", value: "~1 Day (Many Homes)", badge: undefined },
      { icon: "users", label: "Team", value: "2 Installers", badge: undefined },
      { icon: "package", label: "Core System", value: "EPS Bonded Beads", badge: undefined },
      {
        icon: "shield-check",
        label: "Guarantees & Warranties",
        value: "25-Year Insurance-Backed Guarantee",
        badge: undefined,
      },
      { icon: "credit-card", label: "Payment", value: "Only When Work Is Complete", badge: undefined },
    ],
    bottomBannerTitle: "Take The First Step Towards A Warmer Home",
    bottomBannerBody:
      "We'll start by checking whether your property is [[a:suitable for cavity wall insulation]]. From there, you'll receive [[p:clear advice, a works plan]], and guidance on what happens next.",
    ctaText: CAVITY_INSTALL_QUOTATION_CTA,
    ctaHref: "#bottom-cta",
  },

  sectionOrder: [
    "premiumOverview",
    "processClarity",
    "product",
    "trust",
    "testimonials",
    "pricing",
    "faq",
    "ctaLight",
  ],
};
